import React, { Suspense } from 'react';
import { Plus, Loader2, ChevronDown, Star, Sparkles } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useHelpContext } from '@/components/help-widget/HelpContext';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { getCompanyInitials, getCompanyAvatarColor } from '@/lib/utils';
import { PickUpWhereYouLeftOffCard } from '@/components/dashboard/PickUpWhereYouLeftOffCard';
import type { CompanyEpisodeStats } from '@/types/episode.types';
import './MobileDashboard.css';

// Lazy load shell components via Module Federation
const AnnouncementBanner = React.lazy(() => import('shell/AnnouncementBanner'));
const ShellSpotlightCard = React.lazy(() => import('shell/SpotlightCard'));

interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  role: string;
  companyId: string;
  companyName: string;
  defaultLandingPage?: string;
}

interface MobileDashboardProps {
  user: UserProfile | null;
  companyStats: CompanyEpisodeStats[];
  dataReady: boolean;
  isMultiCompany: boolean;
  isDefaultLanding: boolean;
  dashboardSpotlight: any | null;
  onCreateEpisode: (companyId?: string) => void;
  onCreateEpisodeForCompany: (companyId: string) => void;
  onSetDefaultLanding: (checked: boolean) => void;
  onDismissSpotlight: () => void;
  creatingEpisode: boolean;
  isUpdatingPreference: boolean;
}

export const MobileDashboard: React.FC<MobileDashboardProps> = ({
  user,
  companyStats,
  dataReady,
  isMultiCompany,
  isDefaultLanding,
  dashboardSpotlight,
  onCreateEpisode,
  onCreateEpisodeForCompany,
  onSetDefaultLanding,
  onDismissSpotlight,
  creatingEpisode,
  isUpdatingPreference,
}) => {
  const { openPanel, setMode } = useHelpContext();
  const { getCompanyColor } = useUserCompanies();

  const firstName = user?.displayName?.split(' ')[0] || 'there';

  return (
    <div className="pm_mdash-root">
      {/* Announcement Banner */}
      {user && (
        <Suspense fallback={null}>
          <AnnouncementBanner contextTrigger="episode-composer:dashboard" userId={user.id} />
        </Suspense>
      )}

      {/* Header: Greeting + Star */}
      <div className="pm_mdash-header">
        <div className="pm_mdash-greeting">
          <h1>
            Welcome back{user && firstName !== 'there' && (
              <span className="inline animate-fade-in">{`, ${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}`}</span>
            )}
          </h1>
          <p>Here's what's happening with your podcasts.</p>
        </div>

        {dataReady && (
          <button
            className="pm_mdash-star-btn animate-fade-in"
            onClick={() => onSetDefaultLanding(!isDefaultLanding)}
            disabled={isUpdatingPreference || !user}
            aria-label={isDefaultLanding ? 'Remove as default landing page' : 'Set as default landing page'}
          >
            <Star
              style={{
                width: '20px',
                height: '20px',
                color: isDefaultLanding ? 'var(--pm-star-active)' : 'var(--pm-star-inactive)',
                fill: isDefaultLanding ? 'var(--pm-star-active)' : 'transparent',
                transition: 'color 0.15s, fill 0.15s',
              }}
            />
          </button>
        )}
      </div>

      {/* Create Episode Button */}
      {dataReady && (
        <div className="animate-fade-in">
          {isMultiCompany ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="create-episode-trigger"
                  className="pm_mdash-create-btn"
                  disabled={creatingEpisode}
                  style={{
                    backgroundColor: creatingEpisode ? 'var(--pm-btn-primary-hover)' : 'var(--pm-btn-primary-bg)',
                  }}
                >
                  {creatingEpisode ? (
                    <Loader2 style={{ width: '18px', height: '18px' }} className="animate-spin" />
                  ) : (
                    <Plus style={{ width: '18px', height: '18px' }} />
                  )}
                  Create New Episode
                  <ChevronDown style={{ width: '16px', height: '16px' }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {companyStats.map((company) => {
                  const avatarColor = getCompanyColor(company.companyId) || getCompanyAvatarColor(company.companyId);
                  return (
                    <DropdownMenuItem
                      key={company.companyId}
                      data-testid={`company-option-${company.companyId}`}
                      data-company-id={company.companyId}
                      onClick={() => onCreateEpisodeForCompany(company.companyId)}
                      className="flex items-center gap-3 py-2"
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarFallback
                          className="text-[10px] font-medium text-white"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {getCompanyInitials(company.companyName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{company.companyName}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              className="pm_mdash-create-btn"
              disabled={creatingEpisode}
              onClick={() => onCreateEpisode()}
              style={{
                backgroundColor: creatingEpisode ? 'var(--pm-btn-primary-hover)' : 'var(--pm-btn-primary-bg)',
              }}
            >
              {creatingEpisode ? (
                <Loader2 style={{ width: '18px', height: '18px' }} className="animate-spin" />
              ) : (
                <Plus style={{ width: '18px', height: '18px' }} />
              )}
              Create New Episode
            </button>
          )}
        </div>
      )}

      {/* Pick Up Where You Left Off */}
      {dataReady ? (
        <div className="animate-fade-in">
          <PickUpWhereYouLeftOffCard companyStats={companyStats} />
        </div>
      ) : (
        <div style={{ minHeight: '200px' }} />
      )}

      {/* Spotlight or Help Card */}
      {dataReady && (
        <div className="animate-fade-in">
          {dashboardSpotlight ? (
            <Suspense fallback={null}>
              <ShellSpotlightCard spotlight={dashboardSpotlight} onDismiss={onDismissSpotlight} />
            </Suspense>
          ) : (
            <div className="pm_mdash-help-card">
              <div className="relative" style={{ width: '44px', height: '44px' }}>
                <Sparkles
                  className="absolute"
                  style={{ color: 'var(--pm-btn-primary-bg)', width: '28px', height: '28px', top: '0', left: '8px' }}
                />
                <Sparkles
                  className="absolute"
                  style={{ color: 'var(--pm-btn-primary-bg)', width: '12px', height: '12px', top: '4px', left: '0', opacity: 0.7 }}
                />
                <Sparkles
                  className="absolute"
                  style={{ color: 'var(--pm-btn-primary-bg)', width: '10px', height: '10px', bottom: '6px', right: '0', opacity: 0.5 }}
                />
              </div>

              <h3>New to Episode Composer?</h3>

              <p>
                Learn the basics and discover features to speed up your workflow.
              </p>

              <button
                className="pm_mdash-help-link"
                onClick={() => {
                  setMode('browse');
                  openPanel();
                }}
              >
                Browse all help articles
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
